import { useState, useMemo } from "react";

// ─── COPY BUTTON ─────────────────────────────────────────────────────────────
function CopyBtn({ text, t, label = "⎘ Copy" }) {
  const [copied, setCopied] = useState(false);
  return (
    <button className="btn-sm" onClick={() => navigator.clipboard.writeText(text||"").then(()=>{setCopied(true);setTimeout(()=>setCopied(false),1600);})}
      style={{ borderColor:copied?t.statusOk:t.border, color:copied?t.statusOk:t.textMuted }}>
      {copied?"✓ Copied":label}
    </button>
  );
}

function downloadText(content, filename) {
  const blob = new Blob([content], { type:"text/plain" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}

// ─── TEXT DIFF ────────────────────────────────────────────────────────────────
function diffLines(a, b) {
  const linesA = a.split("\n");
  const linesB = b.split("\n");
  const result = [];
  const maxLen = Math.max(linesA.length, linesB.length);
  for (let i = 0; i < maxLen; i++) {
    const la = linesA[i];
    const lb = linesB[i];
    if (la === undefined)       result.push({ type:"added",    val: lb, line: i+1 });
    else if (lb === undefined)  result.push({ type:"removed",  val: la, line: i+1 });
    else if (la !== lb)         result.push({ type:"changed",  valA: la, valB: lb, line: i+1 });
    else                        result.push({ type:"same",     val: la, line: i+1 });
  }
  return result;
}

function TextDiff({ t }) {
  const [left,  setLeft]  = useState("Hello World\nThis is line two\nAnd this is three\nFour is here");
  const [right, setRight] = useState("Hello World\nThis is line 2\nAnd this is three\nFive is here\nNew line added");
  const [showSame, setShowSame] = useState(true);

  const diffs = useMemo(() => diffLines(left, right), [left, right]);
  const added   = diffs.filter(d => d.type === "added").length;
  const removed = diffs.filter(d => d.type === "removed").length;
  const changed = diffs.filter(d => d.type === "changed").length;
  const same    = diffs.filter(d => d.type === "same").length;
  const identical = added === 0 && removed === 0 && changed === 0;

  //const typeColor = { added:t.statusOk, removed:t.statusErr, changed:t.dotAmber, same:t.textMuted };
  //const typeBg    = { added:t.pillGreen, removed:t.pillRed,  changed:t.pillAmber,same:"transparent" };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      <div className="grid-2">
        {[{label:"Text A (Original)",val:left,set:setLeft},{label:"Text B (Modified)",val:right,set:setRight}].map(({label,val,set})=>(
          <div key={label} className="panel" style={{ border:`1px solid ${val.trim()?t.borderActive:t.border}` }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
              <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>{label}</span>
              <button className="btn-sm" onClick={()=>set("")}>Clear</button>
            </div>
            <textarea value={val} onChange={e=>set(e.target.value)} spellCheck={false} placeholder="Paste text here…"
              style={{ width:"100%", height:220, background:"transparent", border:"none", outline:"none", padding:"12px 14px", color:t.textPrimary, fontFamily:"JetBrains Mono, monospace", fontSize:12, lineHeight:1.75, resize:"none" }} />
            <div style={{ padding:"6px 14px", borderTop:`1px solid ${t.border}`, fontSize:11, fontFamily:"JetBrains Mono", color:t.textMuted }}>
              {val.split("\n").length} lines · {val.length} chars
            </div>
          </div>
        ))}
      </div>

      {/* Summary */}
      <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"center" }}>
        {identical ? (
          <div style={{ background:t.pillGreen, border:`1px solid ${t.statusOk}`, borderRadius:10, padding:"10px 18px", color:t.statusOk, fontSize:14, fontWeight:600 }}>✓ Identical texts</div>
        ) : (
          <>
            {[
              {label:`${added} Added`,   color:t.statusOk, bg:t.pillGreen, border:t.statusOk},
              {label:`${removed} Removed`,color:t.statusErr,bg:t.pillRed,  border:t.statusErr},
              {label:`${changed} Changed`,color:t.dotAmber, bg:t.pillAmber,border:t.dotAmber},
              {label:`${same} Same`,      color:t.textMuted, bg:t.bgPanel,  border:t.border},
            ].map(s=>(
              <div key={s.label} style={{ background:s.bg, border:`1px solid ${s.border}`, borderRadius:10, padding:"8px 16px", color:s.color, fontSize:13, fontWeight:600 }}>{s.label}</div>
            ))}
          </>
        )}
        <div onClick={()=>setShowSame(v=>!v)} style={{ display:"flex", alignItems:"center", gap:7, background:t.bgSettingGrp, border:`1px solid ${t.border}`, borderRadius:10, padding:"6px 12px", cursor:"pointer", marginLeft:"auto" }}>
          <div className={`toggle ${showSame?"on":""}`}><div className="toggle-thumb"/></div>
          <span style={{ fontSize:11, color:t.textLabel, fontFamily:"JetBrains Mono" }}>Show unchanged lines</span>
        </div>
      </div>

      {/* Diff output */}
      <div className="panel active-panel">
        <div style={{ padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}`, fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>
          Diff Result
        </div>
        <div style={{ maxHeight:400, overflowY:"auto", fontFamily:"JetBrains Mono", fontSize:12 }}>
          {diffs.filter(d => showSame || d.type !== "same").map((d, i) => (
            <div key={i}>
              {d.type === "same" && (
                <div style={{ padding:"4px 14px", color:t.textMuted, borderBottom:`1px solid ${t.border}` }}>
                  <span style={{ color:t.textMuted, minWidth:36, display:"inline-block" }}>{d.line}</span>{d.val}
                </div>
              )}
              {d.type === "removed" && (
                <div style={{ padding:"4px 14px", background:t.pillRed, borderBottom:`1px solid ${t.border}` }}>
                  <span style={{ color:t.statusErr, minWidth:36, display:"inline-block" }}>−{d.line}</span>
                  <span style={{ color:t.statusErr }}>{d.val}</span>
                </div>
              )}
              {d.type === "added" && (
                <div style={{ padding:"4px 14px", background:t.pillGreen, borderBottom:`1px solid ${t.border}` }}>
                  <span style={{ color:t.statusOk, minWidth:36, display:"inline-block" }}>+{d.line}</span>
                  <span style={{ color:t.statusOk }}>{d.val}</span>
                </div>
              )}
              {d.type === "changed" && (
                <>
                  <div style={{ padding:"4px 14px", background:t.pillRed, borderBottom:`1px solid ${t.border}` }}>
                    <span style={{ color:t.statusErr, minWidth:36, display:"inline-block" }}>−{d.line}</span>
                    <span style={{ color:t.statusErr, textDecoration:"line-through" }}>{d.valA}</span>
                  </div>
                  <div style={{ padding:"4px 14px", background:t.pillGreen, borderBottom:`1px solid ${t.border}` }}>
                    <span style={{ color:t.statusOk, minWidth:36, display:"inline-block" }}>+{d.line}</span>
                    <span style={{ color:t.statusOk }}>{d.valB}</span>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── REMOVE DUPLICATE LINES ───────────────────────────────────────────────────
function RemoveDuplicates({ t }) {
  const [input, setInput]         = useState("apple\nbanana\napple\norange\nbanana\ngrape\norange");
  const [caseSensitive, setCaseSens] = useState(true);
  const [trimLines, setTrimLines]   = useState(true);
  const [removeEmpty, setRemoveEmpty] = useState(false);
  const [sortOutput, setSortOutput]  = useState(false);

  const { output, stats } = useMemo(() => {
    if (!input.trim()) return { output:"", stats:null };
    let lines = input.split("\n");
    if (trimLines) lines = lines.map(l => l.trim());
    if (removeEmpty) lines = lines.filter(l => l.length > 0);
    const seen = new Set();
    const unique = lines.filter(l => {
      const key = caseSensitive ? l : l.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key); return true;
    });
    const sorted = sortOutput ? [...unique].sort((a,b) => a.localeCompare(b, undefined, { sensitivity: caseSensitive?"variant":"accent" })) : unique;
    return {
      output: sorted.join("\n"),
      stats: { original: lines.length, unique: sorted.length, removed: lines.length - sorted.length },
    };
  }, [input, caseSensitive, trimLines, removeEmpty, sortOutput]);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
        {[
          {label:"Case sensitive",  val:caseSensitive, fn:()=>setCaseSens(v=>!v)},
          {label:"Trim whitespace", val:trimLines,     fn:()=>setTrimLines(v=>!v)},
          {label:"Remove empty",    val:removeEmpty,   fn:()=>setRemoveEmpty(v=>!v)},
          {label:"Sort output",     val:sortOutput,    fn:()=>setSortOutput(v=>!v)},
        ].map(({label,val,fn})=>(
          <div key={label} onClick={fn} style={{ display:"flex", alignItems:"center", gap:7, background:t.bgSettingGrp, border:`1px solid ${t.border}`, borderRadius:10, padding:"6px 12px", cursor:"pointer" }}>
            <div className={`toggle ${val?"on":""}`}><div className="toggle-thumb"/></div>
            <span style={{ fontSize:11, color:t.textLabel, fontFamily:"JetBrains Mono" }}>{label}</span>
          </div>
        ))}
      </div>

      {stats && (
        <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
          {[{label:"Input lines",val:stats.original},{label:"Unique lines",val:stats.unique},{label:"Duplicates removed",val:stats.removed}].map(s=>(
            <div key={s.label} style={{ background:t.bgPanel, border:`1px solid ${t.border}`, borderRadius:10, padding:"10px 16px", textAlign:"center", flex:1, minWidth:100 }}>
              <div style={{ fontSize:11, color:t.textMuted, marginBottom:3, textTransform:"uppercase", letterSpacing:"0.06em" }}>{s.label}</div>
              <div style={{ fontSize:20, fontWeight:700, color:t.textAccent, fontFamily:"JetBrains Mono" }}>{s.val}</div>
            </div>
          ))}
        </div>
      )}

      <div className="grid-2">
        <div className="panel" style={{ border:`1px solid ${input.trim()?t.borderActive:t.border}` }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
            <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>Input</span>
            <button className="btn-sm" onClick={()=>setInput("")}>Clear</button>
          </div>
          <textarea value={input} onChange={e=>setInput(e.target.value)} placeholder="Paste lines here (one per line)…" spellCheck={false}
            style={{ width:"100%", height:260, background:"transparent", border:"none", outline:"none", padding:"12px 14px", color:t.textPrimary, fontFamily:"JetBrains Mono, monospace", fontSize:12, lineHeight:1.75, resize:"none" }} />
        </div>
        <div className="panel active-panel">
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
            <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>Output (Unique)</span>
            <div style={{ display:"flex", gap:6 }}>
              {output && <CopyBtn text={output} t={t} />}
              {output && <button className="btn-sm" onClick={()=>downloadText(output,"unique-lines.txt")}>↓ Download</button>}
            </div>
          </div>
          <textarea readOnly value={output} placeholder="Unique lines appear here…" spellCheck={false}
            style={{ width:"100%", height:260, background:"transparent", border:"none", outline:"none", padding:"12px 14px", color:t.textSecondary, fontFamily:"JetBrains Mono, monospace", fontSize:12, lineHeight:1.75, resize:"none" }} />
        </div>
      </div>
    </div>
  );
}

// ─── CASE CONVERTER ───────────────────────────────────────────────────────────
function toCamelCase(str)  { return str.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (_,c)=>c.toUpperCase()); }
function toPascalCase(str) { const c = toCamelCase(str); return c.charAt(0).toUpperCase() + c.slice(1); }
function toSnakeCase(str)  { return str.replace(/\s+/g,"_").replace(/([a-z])([A-Z])/g,"$1_$2").toLowerCase().replace(/[^a-z0-9_]/g,"_").replace(/__+/g,"_"); }
function toKebabCase(str)  { return str.replace(/\s+/g,"-").replace(/([a-z])([A-Z])/g,"$1-$2").toLowerCase().replace(/[^a-z0-9-]/g,"-").replace(/--+/g,"-"); }
function toConstCase(str)  { return toSnakeCase(str).toUpperCase(); }
function toDotCase(str)    { return toSnakeCase(str).replace(/_/g,"."); }
function toTitleCase(str)  { return str.toLowerCase().replace(/(?:^|\s)\w/g,c=>c.toUpperCase()); }
function toSentenceCase(str){ return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase(); }
function toAlternating(str){ return str.split("").map((c,i)=>i%2===0?c.toLowerCase():c.toUpperCase()).join(""); }
function toInvertCase(str) { return str.split("").map(c=>c===c.toUpperCase()?c.toLowerCase():c.toUpperCase()).join(""); }

const CONVERSIONS = [
  { id:"upper",       label:"UPPERCASE",       fn: s => s.toUpperCase() },
  { id:"lower",       label:"lowercase",        fn: s => s.toLowerCase() },
  { id:"title",       label:"Title Case",       fn: toTitleCase },
  { id:"sentence",    label:"Sentence case",    fn: toSentenceCase },
  { id:"camel",       label:"camelCase",        fn: toCamelCase },
  { id:"pascal",      label:"PascalCase",       fn: toPascalCase },
  { id:"snake",       label:"snake_case",       fn: toSnakeCase },
  { id:"kebab",       label:"kebab-case",       fn: toKebabCase },
  { id:"const",       label:"CONSTANT_CASE",    fn: toConstCase },
  { id:"dot",         label:"dot.case",         fn: toDotCase },
  { id:"alternating", label:"aLtErNaTiNg",      fn: toAlternating },
  { id:"invert",      label:"iNVERT cASE",      fn: toInvertCase },
];

function CaseConverter({ t }) {
  const [input, setInput] = useState("Hello World this is a sample text for case conversion");

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      <div className="panel" style={{ border:`1px solid ${input.trim()?t.borderActive:t.border}` }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
          <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>Input Text</span>
          <button className="btn-sm" onClick={()=>setInput("")}>Clear</button>
        </div>
        <textarea value={input} onChange={e=>setInput(e.target.value)} placeholder="Type or paste text to convert…" spellCheck={false}
          style={{ width:"100%", height:100, background:"transparent", border:"none", outline:"none", padding:"12px 14px", color:t.textPrimary, fontFamily:"'Plus Jakarta Sans', sans-serif", fontSize:13, lineHeight:1.7, resize:"none" }} />
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:8 }}>
        {CONVERSIONS.map(conv => {
          const result = input ? conv.fn(input) : "";
          return (
            <div key={conv.id} className="panel" style={{ overflow:"hidden" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"7px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
                <span style={{ fontSize:11, fontWeight:600, color:t.textAccent, fontFamily:"JetBrains Mono" }}>{conv.label}</span>
                <div style={{ display:"flex", gap:5 }}>
                  {result && <CopyBtn text={result} t={t} />}
                  {result && <button className="btn-sm" onClick={()=>setInput(result)} title="Use as input" style={{ fontSize:10 }}>→ Use</button>}
                </div>
              </div>
              <div style={{ padding:"9px 12px", fontFamily:"JetBrains Mono", fontSize:12, color: result ? t.textSecondary : t.textMuted, wordBreak:"break-all", lineHeight:1.6, minHeight:40 }}>
                {result || <span style={{ fontStyle:"italic" }}>—</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── REGEX TESTER ─────────────────────────────────────────────────────────────
const REGEX_PRESETS = [
  { label:"Email",          pattern:"[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}", flags:"g" },
  { label:"URL",            pattern:"https?:\\/\\/(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)", flags:"gi" },
  { label:"Phone (US)",     pattern:"\\+?1?[-. ]?\\(?\\d{3}\\)?[-. ]?\\d{3}[-. ]?\\d{4}", flags:"g" },
  { label:"IPv4",           pattern:"\\b(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\b", flags:"g" },
  { label:"Date (YYYY-MM-DD)", pattern:"\\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\\d|3[01])", flags:"g" },
  { label:"Hex color",      pattern:"#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})", flags:"g" },
  { label:"UUID",           pattern:"[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}", flags:"gi" },
  { label:"ZIP code (US)",  pattern:"\\b\\d{5}(?:-\\d{4})?\\b", flags:"g" },
  { label:"Credit card",    pattern:"\\b(?:\\d[ -]?){13,16}\\b", flags:"g" },
  { label:"HTML tag",       pattern:"<[^>]+>", flags:"g" },
];

function RegexTester({ t }) {
  const [pattern, setPattern] = useState("[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}");
  const [flags, setFlags]     = useState("g");
  const [testStr, setTestStr] = useState("Contact us at hello@example.com or support@minidevtoolkit.com for help.\nAlso reach john.doe+test@company.co.uk");

  const { matches, error, highlighted } = useMemo(() => {
    if (!pattern.trim() || !testStr) return { matches:[], error:null, highlighted:testStr };
    try {
      const rx = new RegExp(pattern, flags);
      const allMatches = [];
      if (flags.includes("g")) {
        let m;
        const rxCopy = new RegExp(pattern, flags);
        while ((m = rxCopy.exec(testStr)) !== null) {
          allMatches.push({ match:m[0], index:m.index, groups: m.groups || {}, captures: [...m].slice(1) });
          if (!flags.includes("g")) break;
        }
      } else {
        const m = rx.exec(testStr);
        if (m) allMatches.push({ match:m[0], index:m.index, groups:m.groups||{}, captures:[...m].slice(1) });
      }
      // Build highlighted HTML (plain text with markers)
      const parts = [];
      let lastIdx = 0;
      const rxH = new RegExp(pattern, flags.includes("g") ? flags : flags+"g");
      let mh;
      while ((mh = rxH.exec(testStr)) !== null) {
        parts.push({ text: testStr.slice(lastIdx, mh.index), type:"normal" });
        parts.push({ text: mh[0], type:"match" });
        lastIdx = mh.index + mh[0].length;
        if (mh[0].length === 0) { rxH.lastIndex++; }
      }
      parts.push({ text: testStr.slice(lastIdx), type:"normal" });
      return { matches: allMatches, error:null, highlighted: parts };
    } catch(e) {
      return { matches:[], error:e.message, highlighted:[] };
    }
  }, [pattern, flags, testStr]);

  const FLAG_OPTS = [
    { f:"g", label:"g", title:"Global — find all matches" },
    { f:"i", label:"i", title:"Ignore case" },
    { f:"m", label:"m", title:"Multiline — ^ and $ match line boundaries" },
    { f:"s", label:"s", title:"Dot all — . matches newlines" },
  ];

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      {/* Pattern input */}
      <div className="panel" style={{ border:`1px solid ${error?t.borderError:pattern.trim()?t.borderActive:t.border}` }}>
        <div style={{ padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}`, fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color: error?t.statusErr:t.textAccent }}>
          Regular Expression
        </div>
        <div style={{ padding:"12px 14px", display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
          <span style={{ fontSize:16, color:t.textMuted, fontFamily:"JetBrains Mono" }}>/</span>
          <input value={pattern} onChange={e=>setPattern(e.target.value)} placeholder="[a-z]+"
            style={{ flex:1, minWidth:200, background:t.bgInput, border:`1px solid ${error?t.statusErr:t.border}`, borderRadius:7, padding:"8px 12px", fontSize:14, fontFamily:"JetBrains Mono", color:t.textPrimary, outline:"none" }} />
          <span style={{ fontSize:16, color:t.textMuted, fontFamily:"JetBrains Mono" }}>/</span>
          <div style={{ display:"flex", gap:4 }}>
            {FLAG_OPTS.map(({f,label,title}) => (
              <button key={f} title={title} onClick={() => setFlags(prev => prev.includes(f) ? prev.replace(f,"") : prev+f)}
                style={{ width:28, height:28, borderRadius:6, border:`1px solid ${flags.includes(f)?t.borderActive:t.border}`, background:flags.includes(f)?t.bgChipActive:"none", color:flags.includes(f)?t.textAccent:t.textMuted, fontSize:12, fontFamily:"JetBrains Mono", cursor:"pointer", fontWeight:600 }}>
                {label}
              </button>
            ))}
          </div>
        </div>
        {error && <div style={{ padding:"6px 14px", borderTop:`1px solid ${t.border}`, fontSize:11, color:t.statusErr, fontFamily:"JetBrains Mono" }}>✕ {error}</div>}
        {!error && matches.length > 0 && <div style={{ padding:"6px 14px", borderTop:`1px solid ${t.border}`, fontSize:11, color:t.statusOk, fontFamily:"JetBrains Mono" }}>✓ {matches.length} match{matches.length!==1?"es":""} found</div>}
        {!error && pattern.trim() && matches.length === 0 && <div style={{ padding:"6px 14px", borderTop:`1px solid ${t.border}`, fontSize:11, color:t.dotAmber, fontFamily:"JetBrains Mono" }}>No matches found</div>}
      </div>

      {/* Presets */}
      <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
        {REGEX_PRESETS.map(p => (
          <button key={p.label} className="btn-sm" onClick={()=>{setPattern(p.pattern);setFlags(p.flags);}}>
            {p.label}
          </button>
        ))}
      </div>

      <div className="grid-2">
        {/* Test string */}
        <div className="panel" style={{ border:`1px solid ${testStr.trim()?t.borderActive:t.border}` }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
            <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>Test String</span>
            <button className="btn-sm" onClick={()=>setTestStr("")}>Clear</button>
          </div>
          <textarea value={testStr} onChange={e=>setTestStr(e.target.value)} placeholder="Paste text to test your regex against…" spellCheck={false}
            style={{ width:"100%", height:240, background:"transparent", border:"none", outline:"none", padding:"12px 14px", color:t.textPrimary, fontFamily:"JetBrains Mono, monospace", fontSize:12, lineHeight:1.75, resize:"none" }} />
        </div>

        {/* Highlighted output */}
        <div className="panel active-panel">
          <div style={{ padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}`, fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>
            Highlighted Matches
          </div>
          <div style={{ padding:"12px 14px", minHeight:240, fontFamily:"JetBrains Mono", fontSize:12, lineHeight:1.75, wordBreak:"break-word", whiteSpace:"pre-wrap" }}>
            {Array.isArray(highlighted) && highlighted.length > 0 ? highlighted.map((part, i) => (
              <span key={i} style={{
                background: part.type === "match" ? t.dotAmber + "44" : "transparent",
                color: part.type === "match" ? t.dotAmber : t.textSecondary,
                borderRadius: part.type === "match" ? 3 : 0,
                outline: part.type === "match" ? `1px solid ${t.dotAmber}` : "none",
                padding: part.type === "match" ? "0 1px" : 0,
              }}>{part.text}</span>
            )) : <span style={{ color:t.textMuted, fontStyle:"italic" }}>Matches highlighted here…</span>}
          </div>
        </div>
      </div>

      {/* Match details */}
      {matches.length > 0 && (
        <div className="panel">
          <div style={{ padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}`, fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>
            Match Details ({matches.length})
          </div>
          <div style={{ maxHeight:240, overflowY:"auto", scrollbarWidth:"thin" }}>
            {matches.map((m, i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:12, padding:"8px 14px", borderBottom:`1px solid ${t.border}` }}>
                <span style={{ fontSize:10, color:t.textMuted, fontFamily:"JetBrains Mono", minWidth:28 }}>#{i+1}</span>
                <span style={{ fontFamily:"JetBrains Mono", fontSize:12, color:t.dotAmber, fontWeight:600, flex:1 }}>{m.match}</span>
                <span style={{ fontSize:10, color:t.textMuted, fontFamily:"JetBrains Mono" }}>index {m.index}</span>
                <span style={{ fontSize:10, color:t.textMuted, fontFamily:"JetBrains Mono" }}>len {m.match.length}</span>
                <CopyBtn text={m.match} t={t} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const TOOLS = [
  { id:"diff",       label:"Text Diff",          icon:"⊕",  desc:"Compare two texts line by line" },
  { id:"dedup",      label:"Remove Duplicates",  icon:"≠",  desc:"Deduplicate lines of text" },
  { id:"case",       label:"Case Converter",     icon:"Aa", desc:"12 case conversion formats" },
  { id:"regex",      label:"Regex Tester",       icon:".*", desc:"Test regular expressions live" },
];

export default function TextToolkit({ t }) {
  const [active, setActive] = useState("diff");

  const components = {
    diff:  <TextDiff t={t} />,
    dedup: <RemoveDuplicates t={t} />,
    case:  <CaseConverter t={t} />,
    regex: <RegexTester t={t} />,
  };

  return (
    <div>
      <div className="tool-hero fade-up">
        <div className="tool-hero-inner">
          <div className="tool-hero-left">
            <h2>✏ Text Utilities</h2>
            <p>Text diff checker, duplicate line remover, 12-format case converter, and live regex tester — all in your browser.</p>
          </div>
          <div className="tool-hero-stats">
            <div className="tool-stat"><div className="tool-stat-val">4</div><div className="tool-stat-lbl">tools</div></div>
            <div className="tool-stat"><div className="tool-stat-val">12</div><div className="tool-stat-lbl">case formats</div></div>
            <div className="tool-stat"><div className="tool-stat-val">0</div><div className="tool-stat-lbl">server calls</div></div>
          </div>
        </div>
      </div>

      <div className="pill-tab-bar" style={{ display:"flex", gap:6, padding:"12px 16px", flexWrap:"wrap" }}>
        {TOOLS.map(tool => {
          const isActive = active === tool.id;
          return (
            <button key={tool.id} onClick={() => setActive(tool.id)}
              className={`pill-tab ${isActive ? "active" : ""}`}>
              <span style={{ fontFamily:"JetBrains Mono", fontSize:11 }}>{tool.icon}</span>{tool.label}
            </button>
          );
        })}
      </div>

      <div className="tool-main">
        {components[active]}
      </div>

      <article className="tool-seo-section" aria-label="About Text Utilities">
        <h2>About Text Utilities</h2>
        <div className="tool-seo-cols">
          <div>
            <h3>How it works</h3>
            <ul>
              <li>Text Diff compares two blocks of text line-by-line in real time.</li>
              <li>Duplicate Remover deduplicates lines with optional sort and trim.</li>
              <li>Case Converter outputs all 12 formats simultaneously from a single input.</li>
              <li>Regex Tester evaluates patterns with flag controls and highlights matches live.</li>
            </ul>
          </div>
          <div>
            <h3>Use cases</h3>
            <ul>
              <li>Compare config files or API responses side by side.</li>
              <li>Clean up CSV exports with duplicate entries.</li>
              <li>Convert variable names between coding conventions (camelCase ↔ snake_case).</li>
              <li>Test and debug regular expressions before using them in code.</li>
            </ul>
          </div>
          <div>
            <h3>Privacy</h3>
            <ul>
              <li>All text processing runs locally in your browser.</li>
              <li>No text is ever sent to any server.</li>
              <li>Free to use with no sign-up required.</li>
            </ul>
          </div>
        </div>
        <h2 style={{ marginTop:"1.5rem" }}>Frequently Asked Questions</h2>
        <details><summary>How does the Text Diff tool work?</summary><p>The diff tool compares your two inputs line by line. Added lines (only in the right) are shown in green, removed lines (only in the left) in red, and changed lines show both old and new versions. A summary at the top counts additions, deletions, and changes.</p></details>
        <details><summary>What case formats does the Case Converter support?</summary><p>The Case Converter outputs 12 formats simultaneously: UPPERCASE, lowercase, Title Case, Sentence case, camelCase, PascalCase, snake_case, kebab-case, CONSTANT_CASE, dot.case, aLtErNaTiNg cAsE, and iNVERTED cASE. Click "Use" on any result to chain conversions.</p></details>
        <details><summary>Can I save my regex patterns?</summary><p>The regex tester includes 10 built-in presets for common patterns (email, URL, phone, UUID, date, hex color, ZIP, credit card, HTML tags, IPv4). You can edit and use them as a starting point for your own patterns.</p></details>
        <details><summary>Does the Text Diff tool send my text to a server?</summary><p>No. The diff algorithm runs entirely in your browser using JavaScript. Your text is never transmitted anywhere. This makes it safe to use with sensitive code, configuration files, or private documents.</p></details>
        <h2 style={{ marginTop:"1.5rem" }}>Alternatives &amp; why miniDevToolkit</h2>
        <p>Compared to <strong>RegExr</strong> — RegExr has a richer regex explanation panel and a community library; miniDevToolkit's regex tester is more lightweight and bundled alongside a diff checker, case converter, and duplicate-line remover in one tool. Compared to <strong>Diffchecker.com</strong> — Diffchecker uploads text to its servers for sharing features; miniDevToolkit's diff runs entirely client-side with no uploads. Compared to <strong>convertcase.net</strong> — miniDevToolkit outputs all 12 case formats simultaneously instead of requiring a separate page load per format. No sign-up, no uploads, fully private.</p>
      </article>
    </div>
  );
}